import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { format } from "date-fns"
import { IconClockSnoozeOutline18 as ClockSnooze } from "nucleo-ui-outline-18"

import { IconButton, Shortcut, SnoozeIcon, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { useBackNavigation } from "@/lib/back-navigation"
import { mutateOk, useZero } from "@/lib/zero"
import { pickMoveOnTarget } from "../../hooks/move-on-target"
import { useThreadsMultiSelectState } from "../../multi-select/threads-multi-select-state"
import { useThreadContext } from "../../thread-context"
import { SnoozeDialog } from "./snooze-dialog"
import { useSnoozeOpener } from "./snooze-opener"
import { formatSnoozeTime } from "./snooze-options"

export function SnoozeButton() {
	const { thread, nextThread, goNext, prevThread, goPrev } = useThreadContext()
	const { goBack } = useBackNavigation()
	const zero = useZero()
	const navigate = useNavigate()
	const { threadIds } = useThreadsMultiSelectState()
	const [isDialogOpen, setIsDialogOpen] = useState(false)

	// Pending state-change dispatches, deferred until the route has changed so
	// Zero's optimistic notify never paints the outgoing thread as SNOOZED.
	const pendingDispatchesRef = useRef<Array<() => void>>([])
	const flushPendingDispatches = useCallback(() => {
		if (pendingDispatchesRef.current.length === 0) return
		const actions = pendingDispatchesRef.current
		pendingDispatchesRef.current = []
		actions.forEach((action) => action())
	}, [])
	useEffect(flushPendingDispatches, [thread.id, flushPendingDispatches])
	useEffect(() => flushPendingDispatches, [flushPendingDispatches])

	const snooze = useCallback(
		(value: Date) => {
			if (threadIds.length > 0) return
			const toastId = `snooze-${thread.id}`
			const threadId = thread.id
			const threadTitle = thread.title
			const prevState = thread.state
			const prevSnoozedUntilIso = thread.snoozedUntil
				? new Date(thread.snoozedUntil).toISOString()
				: null
			const prevLastStateChangeAtIso = thread.lastStateChangeAt
				? new Date(thread.lastStateChangeAt).toISOString()
				: null

			const undoSnooze = async () => {
				await mutateOk(
					zero.mutate(
						mutators.threads.update({
							id: threadId,
							state: prevState,
							snoozedUntilIso: prevSnoozedUntilIso,
							lastStateChangeAtIso: prevLastStateChangeAtIso,
						}),
					),
				)
				void navigate({ to: "/threads/$id", params: { id: threadId } })
			}

			const dispatchStateChange = () => {
				void zero.mutate(
					mutators.threads.update({
						id: threadId,
						state: "SNOOZED",
						snoozedUntilIso: value.toISOString(),
						lastStateChangeAtIso: new Date().toISOString(),
					}),
				).client

				toast.success({
					id: toastId,
					icon: SnoozeIcon,
					title: `Snoozed until ${formatSnoozeTime(value)}`,
					time: 8000,
					descriptionAction: threadTitle
						? {
								label: threadTitle,
								callback: () =>
									void navigate({
										to: "/threads/$id",
										params: { id: threadId },
									}),
							}
						: undefined,
					onUndo: undoSnooze,
				})
			}

			const target = pickMoveOnTarget({
				threadId,
				pathname: window.location.pathname,
				nextThread,
				prevThread,
			})
			if (target === "next") {
				pendingDispatchesRef.current.push(dispatchStateChange)
				goNext()
			} else if (target === "prev") {
				pendingDispatchesRef.current.push(dispatchStateChange)
				goPrev()
			} else {
				goBack("/threads", true)
				dispatchStateChange()
			}
		},
		[
			goBack,
			goNext,
			goPrev,
			nextThread,
			prevThread,
			thread.id,
			thread.title,
			thread.state,
			thread.snoozedUntil,
			thread.lastStateChangeAt,
			navigate,
			threadIds.length,
			zero,
		],
	)

	const openSnoozeDialog = useCallback(() => {
		if (threadIds.length > 0) return
		setIsDialogOpen(true)
	}, [threadIds.length])

	const canSnooze = threadIds.length === 0 && thread.state !== "SNOOZED"
	useEffect(() => {
		if (!canSnooze) return
		useSnoozeOpener.setState({ open: openSnoozeDialog })
		return () => useSnoozeOpener.setState({ open: null })
	}, [canSnooze, openSnoozeDialog])

	const unsnooze = useCallback(async () => {
		if (threadIds.length > 0) return
		await mutateOk(
			zero.mutate(
				mutators.threads.update({
					id: thread.id,
					state: "UNSNOOZED",
				}),
			),
		)
		// for unsnoozing we likely want to stay on the same thread
	}, [zero, thread.id, threadIds.length])

	useShortcut(
		"h",
		() => {
			if (thread.state === "SNOOZED") {
				void unsnooze()
			} else {
				openSnoozeDialog()
			}
		},
		{ enabled: threadIds.length === 0, preventDefault: true },
	)

	const dialog = (
		<SnoozeDialog
			open={isDialogOpen}
			onOpenChange={setIsDialogOpen}
			onSelect={(value) => {
				setIsDialogOpen(false)
				snooze(value)
			}}
		/>
	)

	if (thread.state === "PROCESSED" || !thread.state) {
		return dialog
	}
	if (thread.state === "SNOOZED") {
		return (
			<div className="flex cursor-default select-none items-center gap-3">
				<Tooltip
					label={
						<span className="inline-flex gap-2">
							{`Unsnoozing on ${format(new Date(thread.snoozedUntil!), "EEE, MMM d 'at' h:mm a")}`}
							<Shortcut>{"H"}</Shortcut>
						</span>
					}
				>
					<IconButton
						onClick={unsnooze}
						Icon={SnoozeIcon}
						iconStrokeWidth={1.5}
						variant="ghost"
					/>
				</Tooltip>
			</div>
		)
	}
	return (
		<>
			<Tooltip
				label={
					<span className="inline-flex gap-2">
						{"Snooze"}
						<Shortcut>{"H"}</Shortcut>
					</span>
				}
			>
				<IconButton
					onClick={openSnoozeDialog}
					Icon={ClockSnooze}
					iconStrokeWidth={1.5}
					variant="ghost"
					data-testid="snooze-button"
				/>
			</Tooltip>
			{dialog}
		</>
	)
}
